<template>
  <div class="changelog-container">
    <div
      v-show="!showChangeLogDetails"
      v-change-log-infinite-scroll="loadMoreChangelogs"
      class="changelogs changelogs--md"
      :class="{
        'empty-changelog': !datesShown.length
      }"
    >
      <div
        class="changelog-fixed-title"
        :class="{'invisible': !datesShown.length}"
      >
        <span v-t="'Today'" />
      </div>

      <div
        v-for="(date, index) in datesShown"
        :key="date"
        class="changelog-list-items"
      >
        <div
          class="changelog-date-title"
          :style="`z-index: ${index + 1}`"
          v-text="date"
        />

        <div
          v-for="(changelog, logIndex) in groupedChangelogs[date]"
          :key="`${logIndex}-${changelog.transactionId}`"
          class="changelog-entry-list-item"
          @click="toggleDetails(changelog)"
        >
          <ChangeLogEntry :changelog="changelog" />
        </div>
      </div>

      <div
        class="text-center m-t-10"
      >
        <Loader
          v-if="loadingLogs"
          custom-class="size-sm"
        />
        <span v-if="loadingLogs">
          {{ $t('MESSAGE_LOADING_ACTIVITY') }}
        </span>
        <span v-if="!loadingLogs && (!shouldCheckForMoreLogs || !datesShown.length)">
          {{ $t('MESSAGE_NO_ACTIVITY') }}
        </span>
      </div>
    </div>

    <!-- We can't hide using the if, because this will be outside the popover
     and will close it. So we need to simple hide after the first changelog -->
    <ChangeLogDetails 
      v-if="!!changelogDetails"
      v-show="showChangeLogDetails"
      :show-change-log-details="showChangeLogDetails"
      :changelog="changelogDetails"
      :changelog-related-actions="groupedTransactionIds[changelogDetails.transactionId]"
      :wide="wide"
      @toggle-details="toggleDetails"
    />
  </div>
</template>

<script>
import messages from './locales';
import { mapState, mapGetters, mapActions } from 'vuex';
import ChangeLogEntry from './ChangeLogEntry';
import ChangeLogDetails from './ChangeLogDetails';
import Loader from '@/core/components/Loader';
import moment from 'moment';
import { STATUS_SUCCESS } from '@/store/helpers';
import { accountsService } from '@/angularBridgeCode/ringbaPortal.servicesModule';

export default {
  name: 'ChangeLog',
  components: {
    Loader,
    ChangeLogEntry,
    ChangeLogDetails
  },
  props:{
    objectId: {
      type: String,
      default: ''
    },
    customClass: {
      type: String,
      default: ''
    },
    showNonPrimaryActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {   
      memoizedDateObject: _.memoize(this.returnDateObject),
      groupedTransactionIds: {},
      groupedChangelogs: {},
      datesShown: [],
      loadingLogs: false,
      offset: 0,
      limit: 20,
      changelogDetails: null,
      showChangeLogDetails: false
    };
  },
  computed: {
    ...mapGetters('changeLogs', [
      'getChangeLogsStatus'
    ]),
    ...mapState('changeLogs', [
      'changeLogs'
    ]),
    shouldCheckForMoreLogs(){
      return this.changeLogs.checkMoreLogs;
    },
    endDateForChangeLogRequest(){
      return this.changeLogs.endDateForNextRequest ?? null;
    },
    wide() {
      if (this.objectId && this.objectId.indexOf('CA') === 0) {
        return true;
      }
      return false;
    }
  },
  async created() {
    await this.clearLogs();
    await this.loadMoreLogs();
  },
  methods: {
    ...mapActions('changeLogs', [
      'getChangeLogsByObject',      
      'getChangelogByUsername',      
      'getChangelog',
      'clearLogs'
    ]),
    setDatesToBeShown() {
      const dates = _.sortBy(Object.keys(this.groupedChangelogs), (date) => {
        return this.memoizedDateObject(date);
      });

      return dates.reverse();
    },
    returnDateObject(date) {
      return new Date(date);
    },
    groupLogs() {
      this.changeLogs.logs.forEach(l => {
        const log = {
          ...l
        };
        
        const formattedDate = this.formatDate(log.dtStamp);

        try {
          log.diff = JSON.parse(log.diff);
        } catch (e) {
          //
        }
        
        if ((log.primaryAction || this.showNonPrimaryActions) && log.typeName !== 'RingbaAccount') {
          this.checkIVRCreateLog(log);
          
          this.groupedChangelogs[formattedDate] = this.groupedChangelogs[formattedDate] || [];
          this.groupedChangelogs[formattedDate].push(log);
          this.groupedTransactionIds[log.transactionId] = [];
        } else {
          this.groupedTransactionIds[log.transactionId] = this.groupedTransactionIds[log.transactionId] || [];
          this.groupedTransactionIds[log.transactionId].push(log);
        }
      });
    },
    checkIVRCreateLog(log) {
      if (log && log.typeName == 'IVRTree' && log.diff && log.diff.length === 2 && log.diff[0] === null) {
        log.action = 'Create';     
      }
    },
    formatDate(date) {
      return moment(date).format('MMMM D, YYYY');
    },
    toggleDetails(changelog) {
      this.changelogDetails = changelog ?? this.changelogDetails;
      this.showChangeLogDetails = !this.showChangeLogDetails;
    },
    async loadMoreLogs() {
      this.loadingLogs = true;  

      const data = {
        offset: this.offset,
        limit: this.limit,
        end: this.endDateForChangeLogRequest
      };

      if (this.objectId) {
        await this.getChangeLogsByObject({ objectId: this.objectId, data: data});
      } else if (accountsService.currentUserName) {
        data.userName = accountsService.currentUserName;
        await this.getChangelogByUsername({data: data});
      } else {
        await this.getChangelog({ data: data});
      }

      if(this.changeLogs.logs.length) {
        this.groupLogs();
        this.datesShown = this.setDatesToBeShown();
      }
      
      this.loadingLogs = false;
    },
    async loadMoreChangelogs() {
      if(this.getChangeLogsStatus(this.getActionName()).status == STATUS_SUCCESS && !this.loadingLogs && this.shouldCheckForMoreLogs) {
        this.offset = this.endDateForChangeLogRequest ? 0 : this.offset + this.limit;
        await this.loadMoreLogs();
      }
    },

    getActionName() {
      if (this.objectId) {
        return 'getChangeLogsByObject';
      } else if (accountsService.currentUserName) {
        return 'getChangelogByUsername';
      } else {
        return 'getChangelog';
      }
    }
  },
  i18n: {
    messages,
    locale: 'en',
  }
};
</script>

<style scoped>
.changelogs.empty-changelog {
  min-height: 0;
}
.changelog-entry-list-item, .changelogs, .changelog-container {
   overflow-anchor: none !important;
}
</style>